import Debug from '../../helpers/debug.js';
import ora from 'ora';
import Table from 'cli-table';
import { pr, themed, printJSON } from '../../helpers/print.js';
import { rollbackJob, getEnvJobs } from '../../helpers/api.js';

const debug = (...args) => {
  const debug = Debug('commands:jobs:bulkRollback');
  debug(...args);
};

const command = 'bulk-rollback';

const desc = 'Rollback jobs in bulk';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('from-utc-iso', {
      describe: 'Start date for job search (YYYY-MM-DD)',
      type: 'string',
      alias: ['from'],
      demandOption: true,
    })
    .demandOption('from-utc-iso', 'Date from is required. Example: --from-utc-iso 2024-01-01')
    .option('to-utc-iso', {
      describe: 'End date for job search (YYYY-MM-DD)',
      type: 'string',
      alias: ['to'],
      demandOption: true,
    })
    .demandOption('to-utc-iso', 'Date to is required. Example: --to-utc-iso 2024-01-31')
    .option('tenant-ids', {
      describe: 'Comma-separated list of tenant IDs to filter by',
      type: 'string',
      alias: ['tenants'],
    })
    .option('flow-ids', {
      describe: 'Comma-separated list of flow IDs to filter by',
      type: 'string',
      alias: ['flows'],
    });
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, fromUtcIso, toUtcIso, tenantIds, flowIds } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  try {
    // Validate and format dates
    if (fromUtcIso.length !== 10) {
      const errorMsg = 'Date from must be in YYYY-MM-DD format';
      if (json) {
        printJSON({ status: 'error', error: errorMsg });
      } else {
        spinner.fail(themed(`Error: ${errorMsg}`, 'secondary'));
      }
      process.exit(1);
    }

    if (toUtcIso.length !== 10) {
      const errorMsg = 'Date to must be in YYYY-MM-DD format';
      if (json) {
        printJSON({ status: 'error', error: errorMsg });
      } else {
        spinner.fail(themed(`Error: ${errorMsg}`, 'secondary'));
      }
      process.exit(1);
    }

    // Format dates with time components
    const formattedDateFrom = `${fromUtcIso}T00:00:00Z`;
    const formattedDateTo = `${toUtcIso}T23:59:59.999Z`;

    // 1. Fetch jobs from API
    message = themed(
      `Fetching jobs from ${themed(fromUtcIso, 'info')} to ${themed(toUtcIso, 'info')}...`
    );
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const data = await getEnvJobs({
      debug,
      baseUri: clientApiBaseUri,
      env,
      apikey,
      from: formattedDateFrom,
      to: formattedDateTo,
      count: 100, // Get max jobs per page
    });

    !json && spinner.stop();

    let jobs = data.jobs || [];

    // 2. Filter by tenant IDs if provided
    if (tenantIds) {
      const tenantIdList = tenantIds.split(',').map(t => t.trim()).filter(t => t);
      if (tenantIdList.length > 0) {
        jobs = jobs.filter(job => {
          const jobTenant = job.tenant;
          return tenantIdList.includes(jobTenant);
        });
      }
    }

    // 3. Filter by flow IDs if provided
    if (flowIds) {
      const flowIdList = flowIds.split(',').map(f => f.trim()).filter(f => f);
      if (flowIdList.length > 0) {
        jobs = jobs.filter(job => {
          const jobFlow = job.flow_id || job.flowId || job.flow;
          return flowIdList.includes(jobFlow);
        });
      }
    }

    !json && pr(
      themed(`Found ${themed(String(jobs.length), 'info')} job(s) after filtering.`, 'secondary')
    );

    if (jobs.length === 0) {
      json
        ? printJSON({ status: 'info', message: 'No jobs found matching the filters' })
        : spinner.warn(
            themed(`Warning: ${themed('No jobs found matching the specified filters')}.`, 'secondary')
          );
      return;
    }

    // 4. Rollback all jobs in parallel
    message = themed(`Rolling back ${themed(jobs.length, 'info')} job(s)...`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const results = await Promise.all(
      jobs.map(async (job, index) => {
        const jobRoot = job.s3_root || job.s3Root || job.job_root || job.jobRoot;
        const flow = job.flow_id || job.flowId || job.flow;
        const tenant = job.tenant || job.tenant_id || job.tenantId || job.user_id;
        
        // Extract tap from job data - could be in various fields
        let tap = job.tap;
        if (!tap && job.taps && Array.isArray(job.taps) && job.taps.length > 0) {
          tap = job.taps[0];
        }
        if (!tap && job.tap_name) {
          tap = job.tap_name;
        }
        if (!tap && job.connector) {
          tap = job.connector;
        }

        if (!jobRoot) {
          return {
            index,
            flow,
            tenant,
            ok: false,
            error: {
              message: 'Job root (s3_root) not found in job data'
            },
          };
        }

        if (!tap) {
          return {
            index,
            flow,
            tenant,
            jobRoot,
            ok: false,
            error: {
              message: 'Tap not found in job data'
            },
          };
        }

        try {
          const response = await rollbackJob({
            debug,
            baseUri: clientApiBaseUri,
            env,
            apikey,
            flow,
            user_id: tenant,
            job_root: jobRoot,
            tap,
          });

          return {
            index,
            flow,
            tenant,
            jobRoot,
            tap,
            ok: true,
            response,
          };
        } catch (error) {
          let errorResult;
          
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            const statusCode = error.response.status;
            const statusText = error.response.statusText;
            const responseData = error.response.data;
            const message = error.message;
            
            debug('Error response:', {
              statusCode,
              statusText,
              message,
              data: responseData
            });
            
            errorResult = {
              statusCode,
              statusText,
              message,
              data: responseData
            };
          } else if (error.request) {
            // The request was made but no response was received
            debug('Error: No response received', error.message);
            errorResult = {
              message: error.message,
              request: error.request
            };
          } else {
            // Something happened in setting up the request that triggered an Error
            debug('Error:', error.message);
            errorResult = {
              message: error.message
            };
          }

          return {
            index,
            flow,
            tenant,
            jobRoot,
            tap,
            ok: false,
            error: errorResult,
          };
        }
      })
    );

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 5. Print results
    if (json) {
      printJSON(results);
    } else {
      // Generate results table
      const table = new Table({
        head: ['Index', 'Flow', 'Tenant', 'Job Root', 'Tap', 'Status', 'Response/Error'],
      });

      results.forEach((result) => {
        const status = result.ok ? 'SUCCESS' : 'FAILED';
        const responseOrError = result.ok
          ? JSON.stringify(result.response, undefined, 2)
          : JSON.stringify(result.error, undefined, 2);
        table.push([
          result.index,
          result.flow || 'N/A',
          result.tenant || 'N/A',
          result.jobRoot || 'N/A',
          result.tap || 'N/A',
          status,
          responseOrError,
        ]);
      });

      console.log(table.toString());

      // Print summary
      const successCount = results.filter((r) => r.ok).length;
      const failCount = results.filter((r) => !r.ok).length;
      pr(
        themed(
          `Summary: ${themed(String(successCount), 'info')} succeeded, ${themed(String(failCount), 'error')} failed out of ${themed(
            String(results.length),
            'info'
          )} total.`,
          'secondary'
        )
      );
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err.message, stack: err.stack });
    } else {
      spinner.fail(themed(`Error: ${message || 'Failed to rollback jobs'}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };

